import { numeralFormatter } from 'ns_libs/formatter';
import { ProFormaModelIds } from 'constants/proForma';
import { isApartmentModel, isLandDevelopmentModel, isStudentHousingModel } from 'helpers/proForma';
import { Section as CardSection } from './ExecutiveSummaryCard/types';
import { AREA_PRECISION, NO_VALUE, PERCENTAGE_PRECISION } from './constants';
import {
    calculateDetailParkingMetrics, formatEfficiencyRatio, formatProFormaAddress,
    getLotMixString, getTotalFrontFootage, getTotalLotCount,
} from './helpers';
import { ProFormaSummaryData } from './types';

/**
 * The Details card for `ExecutiveSummaryDetail` as originally setup for every asset type that is
 * not yet on the shared layout. 
 * Apartment is in `layout/details.ts` and the details resolvers
 * Migrate other asset types later to the shared layout.
 */
export const legacyDetailSections = ({
    proForma, incomeSummary, buildableLots, residentialLotMixes, disposition, reportMetrics,
}: ProFormaSummaryData): CardSection[] => {
    const isApartmentWithRetail = proForma?.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;

    const totalUnits = incomeSummary?.totalUnits || 0;
    const totalBeds = incomeSummary?.totalBeds || 0;
    const totalRentableSf = incomeSummary?.totalRentableSf || 0;

    const buildings = proForma?.buildings || 0;
    const stories = proForma?.averageStories || 0;
    const address = formatProFormaAddress(proForma);
    const description = proForma?.description || '';
    const retailBuiltGrossSquareFootage = proForma?.retailBuiltGrossSquareFootage ? Number(proForma.retailBuiltGrossSquareFootage) : 0;
    const grossBuiltSf = proForma?.builtGrossSquareFootage ? Number(proForma.builtGrossSquareFootage) : 0;
    const totalGrossBuiltSf = isApartmentWithRetail ? grossBuiltSf + retailBuiltGrossSquareFootage : grossBuiltSf;
    const efficiencyRatio = formatEfficiencyRatio(totalRentableSf, totalGrossBuiltSf);

    const {
        totalSpots: parkingSpaces, ratio: parkingRatio, suffix: parkingSuffix,
    } = calculateDetailParkingMetrics(proForma, buildableLots, totalUnits);

    const addressItems = [
        ...(address && !proForma?.useCoordinates
            ? [{ id: 'address', label: 'Address', value: address }]
            : []),
        ...(proForma?.useCoordinates && proForma?.latitude != null && proForma?.longitude != null
            ? [{
                id: 'coordinates',
                label: 'Coordinates',
                value: `${proForma.latitude}, ${proForma.longitude}`,
            }]
            : []),
    ];

    const lotMixes = residentialLotMixes?.residentialLotMixes ?? [];
    const buildLandDevelopmentDetailItems = () => {
        const siteAcreage = Number(proForma?.siteAcreage) || 0;
        const totalLotCount = getTotalLotCount(lotMixes);
        const totalFrontFootage = getTotalFrontFootage(lotMixes);
        const saleFrequencyLabel = disposition?.saleFrequency
            ? `${disposition.saleFrequency.charAt(0).toUpperCase()}${disposition.saleFrequency.slice(1)}`
            : NO_VALUE;

        return [
            ...addressItems,
            { id: 'siteAcreage', label: 'Site acreage', value: `${numeralFormatter(siteAcreage, AREA_PRECISION)} acres` },
            { id: 'totalLotCount', label: 'Total lot count', value: numeralFormatter(totalLotCount) },
            { id: 'density', label: 'Density', value: `${numeralFormatter(Number(reportMetrics?.density ?? 0), PERCENTAGE_PRECISION)} lots/acre` },
            { id: 'totalFrontFootage', label: 'Total front footage', value: `${numeralFormatter(totalFrontFootage)} FF` },
            { id: 'lotMix', label: 'Lot mix', value: getLotMixString(lotMixes) || NO_VALUE },
            {
                id: 'avgLotSize',
                label: 'Avg lot size (FF)',
                value: `${numeralFormatter(Number(reportMetrics?.avgLotSizeFrontFootage ?? 0), AREA_PRECISION)} FF`,
            },
            { id: 'saleFrequency', label: 'Sale frequency', value: saleFrequencyLabel },
            { id: 'selloutDuration', label: 'Sellout duration', value: `${numeralFormatter(Number(reportMetrics?.selloutDurationMonths ?? 0))} months` },
        ];
    };

    const standardDetailItems = [
        ...addressItems,
        {
            id: 'landArea',
            label: 'Land area',
            value: `${numeralFormatter(buildableLots.totalLandAreaSquareFootage, AREA_PRECISION)} SF`,
        },
        { id: 'far', label: 'FAR', value: buildableLots.totalFar ? numeralFormatter(buildableLots.totalFar, PERCENTAGE_PRECISION) : NO_VALUE },
        {
            id: 'grossBuildableArea',
            label: 'Gross buildable area',
            value: `${numeralFormatter(buildableLots.totalGrossBuildableArea, AREA_PRECISION)} SF`,
        },
        { id: 'buildings', label: 'Buildings', value: numeralFormatter(buildings) },
        { id: 'stories', label: 'Stories', value: numeralFormatter(stories) },
        ...(isApartmentModel(proForma.proFormaModelId) || isStudentHousingModel(proForma.proFormaModelId)
            ? [{ id: 'units', label: 'Units', value: numeralFormatter(totalUnits) }]
            : []),
        ...(isStudentHousingModel(proForma.proFormaModelId)
            ? [{ id: 'beds', label: 'Beds', value: numeralFormatter(totalBeds) }]
            : []),
        { id: 'grossBuiltSf', label: 'Gross Built SF', value: `${numeralFormatter(totalGrossBuiltSf)} GSF` },
        { id: 'netRentableArea', label: 'Net Rentable Area', value: `${numeralFormatter(totalRentableSf)} RSF` },
        { id: 'efficiencyRatio', label: 'Building efficiency ratio', value: efficiencyRatio ? `${efficiencyRatio}%` : NO_VALUE },
        { id: 'parkingSpaces', label: 'Parking spaces', value: numeralFormatter(parkingSpaces) },
        { id: 'parkingRatio', label: 'Parking ratio', value: parkingRatio ? `${parkingRatio} ${parkingSuffix}` : 'N/A' },
    ];

    const detailSections: CardSection[] = [
        {
            id: 'details',
            title: 'Details',
            items: isLandDevelopmentModel(proForma?.proFormaModelId) ? buildLandDevelopmentDetailItems() : standardDetailItems,
        },
    ];

    if (description && description.trim()) {
        detailSections.push({
            id: 'description',
            title: 'Description',
            items: [
                {
                    id: 'description',
                    value: description,
                    label: 'Description',
                },
            ],
        });
    }

    return detailSections;
};
